"use client";

import { PostHogProvider } from "@posthog/react";
import posthog from "posthog-js";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { PRIVATE_MASK_SELECTOR } from "@/features/analytics/lib/masking";
import type { PosthogBootstrapDataType } from "@/features/analytics/types/posthog.type";

let isPosthogInitialized = false;

type AppPosthogProviderProps = {
  bootstrapData: PosthogBootstrapDataType | null;
  children: ReactNode;
};

export function AppPosthogProvider({
  bootstrapData,
  children,
}: AppPosthogProviderProps) {
  const token = process.env.NEXT_PUBLIC_POSTHOG_TOKEN;

  if (typeof window !== "undefined" && token && !isPosthogInitialized) {
    posthog.init(token, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      bootstrap: bootstrapData ?? undefined,
      capture_pageview: false,
      // Session replay defaults mask input values but NOT rendered text, so
      // addresses, intercom codes and VAT numbers would otherwise be readable
      // in recordings. Sections holding personal data opt in via <Private />.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: PRIVATE_MASK_SELECTOR,
      },
      // Replay masking does not cover autocapture: without these, the text and
      // attributes of a clicked address card still travel as event properties.
      mask_all_text: true,
      mask_all_element_attributes: true,
      // Replay stays off until Cookiebot confirms statistics consent — see
      // <CookiebotConsentGate />. Events and feature flags are not gated.
      disable_session_recording: true,
    });

    isPosthogInitialized = true;
  }

  useEffect(() => {
    if (typeof window !== "undefined" && token) {
      window.posthog = posthog;
    }
  }, [token]);

  if (!token) {
    return children;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
